import React from 'react'
import { BookOpen, ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import type { Guide, Media } from '@/payload-types'
import { getMediaUrl } from '../../lib/media'

interface ResourcesVisaGuidesProps {
  guides: Guide[]
}

const MAX_GUIDES = 6

export function ResourcesVisaGuides({ guides }: ResourcesVisaGuidesProps) {
  if (!guides || guides.length === 0) {
    return null
  }

  const displayedGuides = guides.slice(0, MAX_GUIDES)

  return (
    <section className="py-8 md:py-12 px-6 md:px-12 lg:px-16">
      <div className="max-w-380 mx-auto">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900">Visa Guides</h2>
          <Link
            href="/guides"
            className="flex items-center gap-1.5 text-brand-gold hover:text-brand-gold-light text-sm font-medium transition-colors"
          >
            <span>See all visa guides</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedGuides.map((guide) => {
            const heroImage = guide.heroImage as Media | null | undefined
            const chapters = guide.chapters || []
            const visaType = guide.visaType

            return (
              <Link
                key={guide.id}
                href={`/guides/${guide.slug}`}
                className="group flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {/* Image */}
                <div className="relative aspect-4/3 overflow-hidden bg-brand-blue shrink-0">
                  {getMediaUrl(heroImage, 'card') ? (
                    <Image
                      src={getMediaUrl(heroImage, 'card')}
                      alt={guide.name || 'Guide cover'}
                      fill
                      className="object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-white/20" strokeWidth={1} />
                    </div>
                  )}

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-linear-to-t from-brand-blue/80 via-brand-blue/30 to-transparent" />

                  {/* Visa Type Badge */}
                  {visaType && (
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center px-2.5 py-1 bg-brand-gold/90 rounded-full text-xs font-semibold text-white tracking-wide uppercase">
                        {visaType}
                      </span>
                    </div>
                  )}

                  {/* Chapter Count Badge */}
                  <div className="absolute bottom-3 left-3">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                      <BookOpen className="w-3 h-3 text-brand-gold-light" />
                      <span className="text-xs font-medium text-white/90">
                        {chapters.length} {chapters.length === 1 ? 'Chapter' : 'Chapters'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-5 md:p-6 flex flex-col">
                  <h3 className="text-lg md:text-xl font-serif text-primary mb-1.5 group-hover:text-brand-gold transition-colors leading-snug line-clamp-2">
                    {(guide.title || guide.name).split('\\n').map((line, i, arr) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </h3>

                  {guide.subtitle && (
                    <p className="text-sm text-brand-gold font-medium mb-2">{guide.subtitle}</p>
                  )}

                  {(guide.description || guide.shortDescription) && (
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2 flex-1">
                      {guide.description || guide.shortDescription}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                    {guide.year && (
                      <span className="text-xs text-gray-500 font-medium">
                        Updated {guide.year}
                      </span>
                    )}
                    <div className="flex items-center gap-1.5 text-brand-gold text-sm font-medium ml-auto">
                      <span>Read</span>
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
